import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type LocaleType } from './locales.js';

/**
 * Which language a person is shown when they have chosen none (`docs/concept-internationalisation.md`
 * §4.2): the first of the browser's languages, in order, that FEATit speaks, matched by its language
 * subtag (`de-AT` is `de`); else English. One rule for the web, the desktop and phone shells and the server
 * (`Accept-Language`), so they cannot disagree (IN-4, IN-12): the supported list is the only place that names
 * the languages.
 */
export function negotiateLocale(
  preferred: readonly string[],
  supported: readonly LocaleType[] = SUPPORTED_LOCALES,
): LocaleType {
  for (const tag of preferred) {
    const language = tag.trim().toLowerCase().split(/[-_]/)[0];
    const match = supported.find((candidate) => candidate === language);
    if (match) return match;
  }
  return DEFAULT_LOCALE;
}

/**
 * The languages an `Accept-Language` header names, most preferred first: `de-AT,de;q=0.9,en;q=0.8`. A tag
 * with `q=0` is refused by the sender and left out; equal weights keep the header's order; `*` is no
 * language.
 */
export function parseAcceptLanguage(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(',')
    .map((part, index) => {
      const [tag, ...parameters] = part.trim().split(';');
      const weight = parameters
        .map((parameter) => /^\s*q\s*=\s*([\d.]+)\s*$/i.exec(parameter)?.[1])
        .find((value) => value !== undefined);
      return { tag: tag.trim(), q: weight === undefined ? 1 : Number(weight), index };
    })
    .filter(
      (entry) => entry.tag !== '' && entry.tag !== '*' && entry.q > 0 && !Number.isNaN(entry.q),
    )
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map((entry) => entry.tag);
}
